import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { combineLatest, first, map, of, switchMap } from 'rxjs';
import { filterIsNotUndefined } from '@kaseya-utils';
import { PermissionsFacade } from '@core/permissions/facades/permissions.facade';
import { Permission } from '@core/permissions/models/permission.enum';
import { FirstTimeOnboardingCompletedFacade } from '@layout/first-time-onboarding/facades/first-time-onboarding-completed.facade';
import { AuthorizationService } from '@core/authorization/services/authorization.service';

export const isFirstTimeOnboardingCompletedGuard: CanActivateFn = () => {
  const authorizationService = inject(AuthorizationService);
  const facade = inject(FirstTimeOnboardingCompletedFacade);
  const permissionsFacade = inject(PermissionsFacade);
  const router = inject(Router);

  return permissionsFacade.permissionsLoaded$.pipe(
    first(),
    switchMap(() => {
      if (!permissionsFacade.hasPermission(Permission.OnboardingFirstAdminUserView)() || authorizationService.isMigratedUser()) {
        return of(true);
      }

      facade.loadIsOnboardingCompletedIfNotLoaded();

      return combineLatest([facade.isOnboardingCompleted$, facade.loading$, facade.error$]).pipe(
        map(([hasAccess, isLoading, hasError]) => {
          if (isLoading) {
            return undefined;
          }

          if (hasError) {
            return true;
          }

          if (!hasAccess) {
            return router.createUrlTree(['onboarding']);
          }

          return true;
        }),
        filterIsNotUndefined()
      );
    })
  );
};

export const hasFirstTimeOnboardingAccessGuard: CanMatchFn = () => {
  const facade = inject(FirstTimeOnboardingCompletedFacade);
  const router = inject(Router);

  facade.loadIsOnboardingCompletedIfNotLoaded();

  return combineLatest([facade.isOnboardingCompleted$, facade.loading$, facade.error$]).pipe(
    map(([hasAccess, isLoading, hasError]) => {
      if (isLoading) {
        return undefined;
      }

      if (hasAccess || hasError) {
        return router.createUrlTree(['']);
      }

      return true;
    }),
    filterIsNotUndefined()
  );
};
